<template>

    <landing-page-container :loading="loading" :errorMessage="errorMessage">

        <landing-page
            v-if="showLandingPage"
            v-bind="data"
            :component-data="data.componentData"
        ></landing-page>

        <redirect-url-box
            v-if="showRedirectBanner"
            :url="redirect.url"
            :status="redirect.status"
        ></redirect-url-box>

        <debug v-if="false" :data="data"></debug>

    </landing-page-container>

</template>

<script>

import Store from "@/modules/PanelistProjectEntry/store/index.ts";
import {mapActions, mapState} from 'vuex';
import {fingerprint} from "@/components/mixins/fingerprintMixin.js";
import SimpleError from "@/components/UI/SimpleError.vue";
import LandingPage from "@/components/UI/LandingPage/index.vue";
import LandingPageContainer from "@/components/UI/LandingPage/LandingPageContainer.vue";
import RedirectUrlBox from "@/modules/PanelistProjectExit/components/RedirectUrlBox.vue";
import Debug from "@/components/UI/Debug.vue";
import {webRTCMixin} from "@/components/mixins/WebRTCMixin.js";
import researchDefenderMixin from "@/components/mixins/researchDefenderMixin.js";
import GoogleTagManagerMixin from "@/components/mixins/googleTagManagerMixin.js";

const vuexModule = 'LandingEntry';

export default {

    mixins: [fingerprint, webRTCMixin, researchDefenderMixin, GoogleTagManagerMixin],

    components: {
        Debug,
        RedirectUrlBox,
        LandingPageContainer,
        SimpleError,
        LandingPage
    },

    data() {
        return {
            data: {
                logo: null,
                type: null,
                subPanelId: null,
                locale: null,
                communityUrl: null,
                supportEmail: null,
                translations: null,
                subPanelTranslations: null,
                settings: null,
                componentData: {
                    projectInfo: {
                        welcome_enabled: null,
                        reward_completed: null,
                        loi: null,
                        mobile_friendly: null,
                        tablet_friendly: null,
                        desktop_friendly: null,
                        surveyUrl: null,
                    },
                    settings: {
                        rd_enabled: false,
                        survey_id: null,
                        session_id: null,
                    }
                }
            },
            errorMessage: null,
            surveyLink: null,
            location: null,
            displayError: false,
            loading: true,
            redirect: null,
            fingerprint: null,
        }
    },

    computed: {

        ...mapState(vuexModule, [
            'valid',
            'session',
        ]),

        backgroundColor() {
            if (this.data.settings) {
                return this.data.settings.backgroundColor;
            }
            return "#FFFFFF";
        },

        welcomePageEnabled() {
            if (this.data.componentData.projectInfo) {
                return this.data.componentData.projectInfo.welcome_enabled;
            }

            return false;
        },

        showRedirectBanner() {
            return this.redirect && this.redirect.testSession && !this.welcomePageEnabled && this.redirect.external;
        },

        showLandingPage() {
            if (this.loading) return false;
            if (this.displayError) return true;
            if (this.welcomePageEnabled && this.data.type === 'project-entry') return true;
            if (this.data.type !== 'project-entry') return true;
            return false;
        }
    },

    methods: {
        ...mapActions(vuexModule, [
            'saveFingerprint'
        ]),

        sendValidationRequest() {

            const data = JSON.stringify(this.result);

            let payload = {
                session_id: this.data.componentData.settings.session_id,
                fingerprint: this.fingerprint,
                data: this.encode(data),
            }

            if (this.data.componentData.settings.rd_enabled) {
                payload.rd_data = btoa(JSON.stringify(this.researchDefender));
            }

            this.saveFingerprint(payload)
                .then((response) => {

                    // Component data may not exist if i.e. an error page is displayed
                    if (this.data.componentData.projectInfo) {
                        this.data.componentData.projectInfo.surveyUrl = response.data.redirect.url;
                    }

                    this.redirect = response.data.redirect;

                    if (!this.welcomePageEnabled) {

                        if (response.data.redirect.external && response.data.redirect.testSession) {
                            this.loading = false;
                        } else {
                            this.sendToUrl(response.data.redirect.url);
                        }
                    }
                })
                .catch((error) => {

                    if (error.response) {

                        if (error.response.status === 409) {
                            this.sendToUrl(error.response.data.url);
                            return;
                        }

                        if (error.response.status === 422) {
                            this.errorMessage = error.response.data.message;
                        }

                        if (error.response.status === 429) {
                            this.errorMessage = "Too many attempts, please slow down!";
                        }

                    } else {
                        console.error(error);
                        this.errorMessage = "Error occurred, please try again";
                    }

                    this.displayError = true;
                })
                .finally(() => {
                    this.loading = false;
                });
        },

        encode(str) {
            let encoded = "";
            for (let i = 0; i < str.length; i++) {
                let a = str.charCodeAt(i);
                let b = a ^ 123;
                encoded = encoded + String.fromCharCode(b);
            }
            return encoded;
        },
    },

    mounted() {

        if (this.data.type !== "project-entry") {
            this.loading = false;
            return;
        }

        this.loading = true;

        let asyncTasks = [
            // Replaced by Research Defender
            //this.runWebRTCTest(),
            this.runGenerateFingerprint(),
        ];

        if (this.data.componentData.settings.rd_enabled) {
            asyncTasks.push(this.runResearchDefender(this.data.componentData.settings.survey_id, this.data.componentData.settings.session_id));
        }

        Promise.all(asyncTasks)
            .then((values) => {
                this.fingerprint = values[0].result;
                this.sendValidationRequest();
            })
            .catch((err) => {
                console.error("Unexpected error " + err.toString());
                this.errorMessage = 'Unexpected error. Please contact support for assistance!'
            });

        if (!this.showLandingPage) {
            this.enableGoogleTagManager(this.data.googleTag);
            this.reportPageView(this.data.subPanelName);
        }
    },

    created() {

        if (!this.$store.state[vuexModule]) {
            this.$store.registerModule(vuexModule, Store);
        }

        if (window.Spark.error) {
            this.errorMessage = window.Spark.error;
            this.loading = false;
        } else {
            this.data = window.Spark;
        }
    }
}

</script>
